/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {DropsheetOptions} from '../types/templates';
import {process} from './ExcelHelpers';

const noop = () => {};

/**
 * The dropsheet function provides a single point of access for facilitating
 * drag and drop import and parsing operations of an Excel Spreadsheet within
 * the UI.
 *
 * ```typescript
 *
 *   dropsheet({
 *     target      : document.getElementById('drop-target'),
 *     onComplete  : this.importComplete
 *     onFailed    : this.importFailed,
 *     onSizeError : this.importSizeError,
 *     converter   : this.headersToAttributes,
 *     parse       : true
 *   });
 * ```
 *
 * @param  {object} opts Supported Drag and Drop status and parsing
 *         options.
 * @param  {object} options.target The drag and drop target element.
 * @param  {function} options.onComplete A handler to be invoked when the
 *         operation has been completed. The function will be invoked within
 *         the following argument:
 *           items: The parsed spreadsheet which contains the spreadsheet
 *           headers as the first element, and each spreadsheet row as the
 *           additional elements.
 *           workbook: The xlsx workbook which contains all information
 *           related to the uploaded spreadsheet.
 *           workbook: The xlsx workbook which contains all information
 *           related to the uploaded spreadsheet.
 * @param  {function} options.onFailed A handler which will be invoked if
 *         the operation fails.
 * @param  {boolean} options.parse By default, the imported spread sheet will
 *         be converted to an array of arrays, whereby the first array contains
 *         the spreadsheet column headers, and the subsequent arrays represent
 *         each row within the spreadsheet. This can then be parsed freely for
 *         further processing (such as sending the results to a Service, etc.)
 *         However, the processing of the results can be handled automatically
 *         when this option is set to true, in which case and array of objects
 *         will be returned with each items values mapped to each header property.
 * @param  {function} options.converter A converter function which is to be
 *          used for converting column header names to entity attribute names.
 *          Note, this option is ignored if options.parse is false, otherwise,
 *          if options.parse is true and a converter function is provided, the
 *          function will be invoked with each column name for processing column
 *          header names to entity attribute names. By default, if a converter
 *          function is not provided, the header names are converted to camelCase
 *          when parsing the results. Use this option for providing more robust
 *          conversions.
 * @param  {function} options.importSizeError A handler which will be invoked.
 *         if the imported file is larger then the allowed maximum file size.
 */
export const dropsheet = (
  options: DropsheetOptions,
  setImportStatus: Function,
  isDevice: boolean = false
) => {
  const opts     = dropsheetOptions(options);
  const {target} = opts;
  if (target?.addEventListener) {
    const handleDrop = (evt) => {
      setImportStatus(true);
      process(defer(evt).dataTransfer.files, opts, isDevice);
    };
    const handleDragover = (evt) => {
      defer(evt).dataTransfer.dropEffect = 'copy';
    };
    target.addEventListener('dragenter', handleDragover, false);
    target.addEventListener('dragover', handleDragover, false);
    target.addEventListener('drop', handleDrop, false);
  }
};

/**
 * @private
 */
const defer = (evt: DragEvent): DragEvent => {
  evt.stopPropagation();
  evt.preventDefault();
  return evt;
};

/**
 * @function dropsheetOptions
 *
 * Creates the options to be used within the dropsheet function
 * by providing default options where required and / or optional params
 * have not been explicitly provided.
 */
const dropsheetOptions = ({
  target,
  maxEntries = 1e6,
  onComplete = noop,
  onFailed = noop,
  onSizeError = noop,
  converter = noop,
  parse = false,
  isAssetBulk = false,
  convertCSVToJSON = false
}: DropsheetOptions): DropsheetOptions => {
  return {
    target,
    maxEntries,
    onComplete,
    onFailed,
    onSizeError,
    converter,
    parse,
    isAssetBulk,
    convertCSVToJSON
  };
};

export let importStatus = false;

export const setImportStatus = (status: boolean): void => {
  importStatus = status;
};
